import type { OrganizationMemberRole } from '@/lib/services/organization-api';
import { orgUrl, stripOrgSlug } from '@/lib/org-path';

/**
 * One person can hold a different role in every organization they belong to.
 * These helpers decide what a membership means for navigation: staff get the
 * organization's workspace, students get their college home inside the
 * personal workspace (their career and their placements live together).
 */

export const MEMBER_ROLE_LABEL: Record<OrganizationMemberRole, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  HOD: 'Head of department',
  HIRING_MANAGER: 'Hiring manager',
  RECRUITER: 'Recruiter',
  INTERVIEWER: 'Interviewer',
  PLACEMENT_OFFICER: 'Placement officer',
  INTERNAL_FACULTY: 'Faculty',
  EXTERNAL_FACULTY: 'Visiting faculty',
  STAFF: 'Staff',
  STUDENT: 'Student',
  MEMBER: 'Member',
};

export function memberRoleLabel(role: string): string {
  return MEMBER_ROLE_LABEL[role as OrganizationMemberRole] ?? role.replace(/_/g, ' ').toLowerCase();
}

export const STUDENT_HOME = '/personal/my-college';

/** The parts of a membership (or a pending invitation) these rules read. */
type Membership = { role: string; status: string; organization: { type: string } };

export function isStudentMembership(m: Membership): boolean {
  return m.organization.type === 'COLLEGE' && m.role === 'STUDENT';
}

/** Active memberships that open an organization workspace (everything but students). */
export function isWorkspaceMembership(m: Membership): boolean {
  return m.status === 'ACTIVE' && !isStudentMembership(m);
}

/**
 * Where selecting a membership in the workspace switcher lands. Workspace URLs
 * name the organization (/college/<slug>/…), so pass its slug; without one the
 * short form is returned and resolved to an organization on arrival.
 */
export function membershipHome(m: Membership & { organization: { slug?: string | null } }): string {
  if (isStudentMembership(m)) return STUDENT_HOME;
  const ctx = m.organization.type === 'COMPANY' ? 'company' : 'college';
  return m.organization.slug ? orgUrl(ctx, m.organization.slug) : `/${ctx}/dashboard`;
}

/**
 * The personal page a student is sent to when they open a staff-only college
 * URL (an old bookmark, or a link a classmate shared).
 */
export function studentEquivalent(fullPathname: string): string {
  const pathname = stripOrgSlug(fullPathname);
  if (pathname.startsWith('/college/drives')) return '/personal/drives';
  if (pathname.startsWith('/college/notices')) return '/personal/notices';
  if (pathname.startsWith('/college/messages')) return '/personal/messages';
  return STUDENT_HOME;
}
